import { ElementRef } from '@angular/core';
import { ChartXY, SeriesXY, SolveResultSegmentSeries, SolveResultXY } from '@lightningchart/lcjs';
import { BasicSeriesData } from 'app/shared/models/hydrographNEW';
import { StatusCodeService } from 'app/shared/services/status-code.service';
import { Subscription } from 'rxjs';
import { DEPTH_ENTITY, RAW_VELOCITY_ENTITY, VELOCITY_ENTITY } from 'app/shared/constant';
import { TOOLTIP_ARROW_STYLES, TOOLTIP_DEFAULT_PRECISION, TOOLTIP_ELEMENT_STYLES } from 'app/shared/models/blockage-prediction';
import { LightningChartsTooltipFactory } from 'app/shared/components/hydrograph/lightning-chart-builder/lightning-chart-tooltip-factory';
import { DateutilService } from 'app/shared/services/dateutil.service';
import moment from 'moment';

const TOOLTIP_OFFSET_X = 20;
const TOOLTIP_OFFSET_Y = 40;

export class BpTooltip {
    private tooltipElement: HTMLDivElement;
    private arrowElement: HTMLDivElement;
    arrows: HTMLDivElement[] = [];

    private chart: ChartXY;
    private container: HTMLElement;

    private isDarkTheme: boolean;
    private subscriptions: Subscription[] = [];

    constructor(
        private elementRef: ElementRef,
        private seriesData: BasicSeriesData[],
        private statusCodeService: StatusCodeService,
        private dateutilService: DateutilService,
        private lcTooltipFactory?: LightningChartsTooltipFactory
    ) {
        this.container = this.elementRef.nativeElement.querySelector('#container');
        this.createTooltipElements();

        this.subscriptions.push(
            this.statusCodeService.userInfoThemeBS.subscribe((isDarkTheme: boolean) => {
                this.dispose(false);
                this.isDarkTheme = isDarkTheme;
                this.createTooltipElements()
            })
        )
    }

    private createTooltipElements() {
        // Create tooltip container
        this.tooltipElement = document.createElement('div');
        Object.assign(this.tooltipElement.style, TOOLTIP_ELEMENT_STYLES(this.isDarkTheme));

        // Add elements to container
        this.container.appendChild(this.tooltipElement);

        if(!this.lcTooltipFactory) {
            // Create arrow indicator
            this.arrowElement = document.createElement('div');
            Object.assign(this.arrowElement.style, TOOLTIP_ARROW_STYLES);
            this.container.appendChild(this.arrowElement);
        }
    }

    public setChart(chart: ChartXY) {
        this.chart = chart;
        this.container = this.chart.engine.container;
    }

    public setSeriesData(seriesData: BasicSeriesData[]) {
        this.seriesData = seriesData;
    }

    public showHydrographTooltip(mouseEvent: MouseEvent, nearestPoints: { point: SolveResultXY, series: SeriesXY }[]) {
        if (!this.chart || !nearestPoints.length) return;
        const content = this.formatNearestPointsContent(mouseEvent, nearestPoints);
        const mousePoint = this.getMousePoint(mouseEvent);
        this.displayTooltip(content, mousePoint);
    }

    private formatNearestPointsContent(clientCoords: { clientX: number, clientY: number }, nearestPoints: { point: SolveResultXY, series: SeriesXY }[]): string {
        let content = '';
        const boundingClientRect = this.chart.engine.container.getBoundingClientRect();

        nearestPoints.forEach((item, index) => {
            const series = this.seriesData.find(v => item && v.entityName === item.series.getName());

            const mouseLocationAxis = item.series.chart.translateCoordinate(clientCoords, { x: item.series.axisX, y: item.series.axisY });

            // #45758 Find closest timestamp in data to a mouse over point
            const nearestPointX = nearestPoints.flat().reduce((prev, curr) => {
                if (!prev) return curr;
                if (!curr) return prev;
                return Math.abs(mouseLocationAxis.x - curr.point.x) <= Math.abs(mouseLocationAxis.x - prev.point.x) ? curr : prev;
            });

            // #45758 It is not nearest point but point that has exactly same timestamp
            const point = series.data.find(p => p.x === nearestPointX.point.x);
            if (series && point) {

                const value = point.y.toFixed(series.precision || TOOLTIP_DEFAULT_PRECISION);
                content += `
                    <div style="margin: 4px 0;">
                        <span style="color: ${series.color}">●</span>
                        <span>${series.entityName}: ${value} ${series.unitOfMeasure || ''}</span>
                    </div>
                `;


                if(this.lcTooltipFactory) {
                    const chartSeries = this.chart.getSeries().find(s => s.getName() === series.entityName);

                    const nearestPointClient = this.chart.translateCoordinate(
                        { x: point.x, y: point.y },
                        { x: chartSeries.axisX, y: chartSeries.axisY },
                        this.chart.coordsClient
                    );

                    const relativePosition = {
                        clientX: nearestPointClient.clientX - boundingClientRect.left,
                        clientY: nearestPointClient.clientY - boundingClientRect.top
                    }

                    this.createArrow(index);
                    this.lcTooltipFactory.arrowStyle(this.arrows[index], {x: relativePosition.clientX, y: relativePosition.clientY}, series.color);
                    this.arrowShow(index, true);
                }
            } else {
                if(this.lcTooltipFactory) {
                    this.arrowShow(index, false);
                }
            }
        });

        if (nearestPoints[0] && nearestPoints[0].point) {
            const momentDate = moment.utc(nearestPoints[0].point.x);
            content = `<div style="margin-bottom: 8px">${momentDate.format(this.dateutilService.formatTooltipForCharts())}</div>` + content;
        }

        return content;
    }

    public arrowShow(index: number, isVisible: boolean) {
        if(!this.arrows[index]) return;
        this.lcTooltipFactory.arrowShow(this.arrows[index], isVisible);
    }

    public createArrow(index: number) {
        if(this.arrows[index]) return;

        const arrow = this.lcTooltipFactory.createArrow(this.container);

        this.arrows[index] = arrow;
    }

    private getMousePoint(mouseEvent: MouseEvent) {
        const boundingRect = this.container.getBoundingClientRect();
        return {
            x: mouseEvent.clientX - boundingRect.left,
            y: mouseEvent.clientY - boundingRect.top
        };
    }

    private displayTooltip(content: string, mousePoint: { x: number, y: number }) {
        this.tooltipElement.innerHTML = content;
        this.tooltipElement.style.display = 'block';

        let tooltipX = mousePoint.x + TOOLTIP_OFFSET_X;
        const tooltipY = mousePoint.y - TOOLTIP_OFFSET_Y;

        const boundingRect = this.container.getBoundingClientRect();
        if (tooltipX + this.tooltipElement.offsetWidth > boundingRect.width) {
            tooltipX = mousePoint.x - this.tooltipElement.offsetWidth - TOOLTIP_OFFSET_X;
        }

        this.tooltipElement.style.left = `${tooltipX}px`;
        this.tooltipElement.style.top = `${tooltipY}px`;

        if(!this.lcTooltipFactory) {
            this.arrowElement.style.display = 'block';
            this.arrowElement.style.left = `${mousePoint.x}px`;
            this.arrowElement.style.top = `${mousePoint.y}px`;
        }

    }

    public showScatterTooltip(mouseEvent: MouseEvent, nearestPoints: SolveResultXY[]) {
        if (!this.chart || !nearestPoints.length) return;

        const depthSeries = this.seriesData.find(s => s.entityId === DEPTH_ENTITY);
        const velocitySeries = this.getVelocityEntity();
        const point = nearestPoints[0];

        if (!depthSeries || !velocitySeries || !point) return;

        const content = `
            <div style="padding: 8px">
                <div style="margin-bottom: 4px">
                    <span style="color: ${depthSeries.color || '#000'}">●</span>
                    <span>${depthSeries.entityName}: ${point.y.toFixed(depthSeries.precision || TOOLTIP_DEFAULT_PRECISION)} ${depthSeries.unitOfMeasure || ''}</span>
                </div>
                <div>
                    <span style="color: ${velocitySeries.color || '#000'}">●</span>
                    <span>${velocitySeries.entityName}: ${point.x.toFixed(velocitySeries.precision || TOOLTIP_DEFAULT_PRECISION)} ${velocitySeries.unitOfMeasure || ''}</span>
                </div>
            </div>
        `;
        const mousePoint = this.getMousePoint(mouseEvent);
        this.displayTooltip(content, mousePoint);
    }

    public hide() {
        this.tooltipElement.style.display = 'none';

        if(this.lcTooltipFactory) {
            for (let i = 0; i < this.arrows.length; i++) {
                this.arrowShow(i, false);
            }
        } else {
            this.arrowElement.style.display = 'none';
        }
    }

    public dispose(unsubscribe: boolean = true) {
        this.tooltipElement?.remove();
        this.arrowElement?.remove();

        for(const a of this.arrows) {
            a.remove();
        }
        this.arrows = [];

        if (unsubscribe) {
            this.subscriptions.forEach(v => v.unsubscribe());
        }
    }

    private getVelocityEntity(): BasicSeriesData | undefined {
        return (
            this.seriesData.find(v => v.entityId === RAW_VELOCITY_ENTITY) ||
            this.seriesData.find(v => v.entityId === VELOCITY_ENTITY)
        );
    }
}
